<script setup lang="ts">
import {
  SignatureGuideAppleMailIos,
  SignatureGuideAppleMailMac,
  SignatureGuideGmail,
  SignatureGuideOutlookLegacy,
  SignatureGuideOutlookMac,
  SignatureGuideOutlookWeb,
} from '#components'

// Navigation metadata; each guide's content lives in its own component.
// Videos are the Walker•Drawas ones for now: same steps, to be re-recorded for No. J.
const guides = [
  { id: 'gmail', title: 'Gmail', subtitle: 'Web (mail.google.com)', icon: 'i-icon-logo-gmail', videoId: '1CAKOKvPZHWtG32HV8nM8dyB48MBFUfQR', component: SignatureGuideGmail },
  { id: 'outlook-web', title: 'Outlook', subtitle: 'Web (outlook.live.com / outlook.office.com)', icon: 'i-icon-logo-outlook', videoId: '1BEJhSOg40tJHt1P7UZ8PT4sRnwnWryW6', component: SignatureGuideOutlookWeb },
  { id: 'outlook-mac', title: 'Outlook', subtitle: 'New desktop app for macOS', icon: 'i-icon-logo-outlook', videoId: '1p-9IVmZr8xD09ZLfrRip2RPVAQSFzR8r', component: SignatureGuideOutlookMac },
  { id: 'outlook-legacy', title: 'Outlook', subtitle: 'Desktop app for Windows / Mac legacy', icon: 'i-icon-logo-outlook', videoId: '1Xj29Oe8Af1tz3VvJtTkP_VcK5OFpSeXe', component: SignatureGuideOutlookLegacy },
  { id: 'ios-mail', title: 'Apple Mail', subtitle: 'iPhone / iPad (iOS)', icon: 'i-icon-logo-apple', videoId: '1IfV-y3hj9CkRuLsixvwGp5M0ccEEhOmO', component: SignatureGuideAppleMailIos },
  { id: 'apple-mail-mac', title: 'Apple Mail', subtitle: 'macOS', icon: 'i-icon-logo-apple', videoId: '1pv2n2w8zZm49VcqM-rV0IloQGKXnrUTN', component: SignatureGuideAppleMailMac },
]

const activeId = ref(guides[0]!.id)

// The active guide is the last one whose top has scrolled past the upper third of the viewport
function updateActive() {
  const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
  if (atBottom) {
    activeId.value = guides.at(-1)!.id
    return
  }
  const threshold = window.innerHeight / 3
  let current = guides[0]!.id
  for (const guide of guides) {
    const el = document.getElementById(guide.id)
    if (el && el.getBoundingClientRect().top <= threshold)
      current = guide.id
  }
  activeId.value = current
}

useEventListener('scroll', updateActive, { passive: true })
onMounted(updateActive)

const activeGuide = computed(() => guides.find(g => g.id === activeId.value)!)

// Mobile only: the sidebar collapses into a burger menu
const menuOpen = ref(false)
const sidebar = useTemplateRef<HTMLElement>('sidebar')

onClickOutside(sidebar, () => {
  menuOpen.value = false
})
onKeyStroke('Escape', () => {
  menuOpen.value = false
})

function goTo(id: string) {
  menuOpen.value = false
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  history.replaceState(history.state, '', `#${id}`)
}
</script>

<template>
  <div class="mx-auto px-6 pb-12 gap-0 grid grid-cols-1 max-w-300 w-full lg:gap-12 lg:grid-cols-[240px_minmax(0,1fr)]">
    <!-- Mobile: sticky bar with a burger menu. lg+: sticky sidebar. -->
    <aside
      ref="sidebar"
      class="border-b border-black/10 bg-ivory top-0 sticky z-10 -mx-6 lg:mx-0 lg:pt-8 lg:border-b-0 lg:static"
    >
      <button
        type="button"
        class="typo-sans-1 px-6 py-3.5 text-left flex gap-2.5 w-full cursor-pointer items-center lg:hidden"
        :aria-expanded="menuOpen"
        aria-controls="docs-nav"
        @click="menuOpen = !menuOpen"
      >
        <span :class="menuOpen ? 'i-icon-x' : 'i-icon-list'" class="flex-none size-5 icon" aria-hidden="true" />
        <span class="flex-none size-5 icon" :class="activeGuide.icon" aria-hidden="true" />
        <span>
          {{ activeGuide.title }}
          <span class="c-black/50 ml-1">{{ activeGuide.subtitle }}</span>
        </span>
      </button>

      <nav
        id="docs-nav"
        class="px-4 pb-4 pt-2 motion-snug border-b border-black/10 bg-ivory flex flex-col gap-1 shadow-[0_12px_24px_rgba(0,0,0,0.08)] inset-x-0 top-full absolute lg:p-0 lg:border-b-0 lg:opacity-100 lg:visible lg:shadow-none lg:translate-y-0 lg:top-8 lg:sticky"
        :class="menuOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'"
      >
        <p class="typo-sans-1 c-black/50 mx-3 my-2 uppercase lg:mx-0 lg:mt-0">
          Email clients
        </p>
        <a
          v-for="guide in guides"
          :key="guide.id"
          :href="`#${guide.id}`"
          class="typo-sans-1 px-3 py-2.5 motion-snug rounded-2 flex gap-3 items-center"
          :class="activeId === guide.id ? 'bg-black c-white' : 'c-black hover:bg-linen'"
          @click.prevent="goTo(guide.id)"
        >
          <!-- The Apple logo is black: invert it on the active (black) row -->
          <span
            class="flex-none size-5 icon"
            :class="[guide.icon, { invert: activeId === guide.id && guide.icon === 'i-icon-logo-apple' }]"
            aria-hidden="true"
          />
          <span>
            {{ guide.title }}
            <span class="opacity-60 block">{{ guide.subtitle }}</span>
          </span>
        </a>
      </nav>
    </aside>

    <main class="pt-8 min-w-0">
      <h1 class="typo-serif-3 mb-4">
        How to install your signature
      </h1>
      <p class="typo-sans-2 c-black/70">
        Generate your signature, copy it with <b>Copy Signature</b> and follow the guide for your email client.
        Each guide includes a short video walkthrough.
      </p>

      <SignatureDocsGuide
        v-for="{ component, ...meta } in guides"
        :key="meta.id"
        v-bind="meta"
      >
        <component :is="component" />
      </SignatureDocsGuide>
    </main>
  </div>
</template>
